export default function GuidelinesModal({ isOpen, onClose }) {
    if (!isOpen) return null;

    // Close when clicking the backdrop but not the modal content
    const handleBackdropClick = (e) => {
        if (e.target.classList.contains("guidelines__backdrop")) {
            onClose?.();
        }
    };

    return (
        <div
            className="guidelines__backdrop"
            role="dialog"
            aria-modal="true"
            aria-labelledby="guidelines-title"
            onClick={handleBackdropClick}
        >
            <div className="guidelines__modal">
                <header className="guidelines__header">
                    <h1 id="guidelines-title" className="guidelines__title">SOLE Editorial Guidelines</h1>
                    <button className="guidelines__close" onClick={onClose} aria-label="Close">×</button>
                </header>

                <div className="guidelines__body">
                    <section className="guidelines__section">
                        <h2>Purpose</h2>
                        <p>
                            Keep slides clean and credible. Use <em>inline citations</em> in parentheses for Bible verses, Catechism paragraphs, and direct quotations. Use numbered <em>superscript</em> footnotes for other sourcing (ideas/paraphrases, data, statistics), and collect those on a single end slide called <strong>“References”</strong>. URLs should be <strong>hyperlinked</strong> on titles or source names, not printed in full.
                        </p>
                    </section>

                    <section className="guidelines__section">
                        <h2>Inline Citations <span style={{ fontWeight: 'normal' }}>(All citations follow AP style unless otherwise noted.)</span></h2>
                        <p>Use parentheses <em>inline</em> when you are quoting Scripture, the Catechism, or quoting a book/article/media. Do <strong>not</strong> also add a footnote number for these.</p>
                        <ul className="guidelines__list">
                            <li>
                                <strong>Bible:</strong> (Book Chapter:Verse–Verse, Translation)
                                <div className="guidelines__example">Example: (John 6:53–56, <em>RSV‑2CE</em>).</div>
                            </li>
                            <li>
                                <strong>Catechism:</strong> (CCC paragraph)
                                <div className="guidelines__example">Example: (CCC 1376).</div>
                            </li>
                            <li>
                                <strong>Books / Articles / Media:</strong> Author, <em>Title</em>. Hyperlink the title. If a page number or timestamp is important, include it on the References slide.
                                <div className="guidelines__example">Example (print): Augustine, <em>Confessions</em>.</div>
                                <div className="guidelines__example">Example (media): Bishop Robert Barron, “Evangelizing the Culture”.</div>
                            </li>
                        </ul>
                    </section>

                    <section className="guidelines__section">
                        <h2>Footnote Citation Style</h2>
                        <p>End-slide references follow AP style formatting and are organized by source type as follows (used for paraphrases, statistics, and general sourcing):</p>
                        <ul className="guidelines__list">
                            <li>
                                <strong>Church documents (Vatican, councils, papal docs):</strong> Author/Body, <em>Title</em>, §section (Year). Link the title to the official source (Vatican, USCCB, etc.).
                                <div className="guidelines__example">Example: Pope St. John Paul II. <em>Ecclesia de Eucharistia</em>, §17 (2003).</div>
                            </li>
                            <li>
                                <strong>Books:</strong> Author, <em>Title</em> (Year), page(s) if relevant. <span className="guidelines__note">Publisher optional.</span>
                                <div className="guidelines__example">Example: Trent Horn. <em>Persuasive Pro‑Life</em> (2014), p. 57.</div>
                            </li>
                            <li>
                                <strong>Articles / Web:</strong> Author, “Title,” <em>Site/Publication</em>, Date. Link the title or site.
                                <div className="guidelines__example">Example: Edward Feser. “The Immateriality of the Intellect.” <em>Edward Feser Blog</em>, March 15, 2020.</div>
                            </li>
                            <li>
                                <strong>Speeches / Interviews:</strong> Speaker, “Title or topic,” <em>Event/Show</em>, Organization, Date. Link title/source; include timestamp if helpful.
                                <div className="guidelines__example">Example: Bishop Robert Barron. “Evangelizing the Culture.” <em>Word on Fire Podcast</em>, April 5, 2022.</div>
                            </li>
                            <li>
                                <strong>Personal communication:</strong> Name, description of communication (conversation/email), Date. Mark as personal communication; role optional.
                                <div className="guidelines__example">Example: John Smith, personal communication, September 10, 2025.</div>
                            </li>
                        </ul>
                    </section>

                    <section className="guidelines__section">
                        <h2>When to Use Inline vs. Footnote Citations</h2>
                        <ul className="guidelines__list">
                            <li><strong>Inline</strong> → Use for <em>direct quotes</em> of the Bible, the Catechism, and quotations from books/articles/media.</li>
                            <li><strong>Footnote citations (References slide)</strong> → Use for <em>ideas/paraphrases</em>, data, statistics, historical claims, or when summarizing an argument drawn from a source.</li>
                            <li><strong>Both (optional)</strong> → For a direct quote from a longer work, you may use an inline citation <em>and</em> also include the full entry on the References slide for completeness.</li>
                        </ul>
                    </section>

                    <section className="guidelines__section">
                        <h2>Bullets & Punctuation</h2>
                        <ul className="guidelines__list">
                            <li>Use <strong>periods</strong> only when a bullet is a complete sentence. No period for fragments.</li>
                            <li>Keep bullets parallel in structure; aim for ≤ 2 lines each.</li>
                            <li>Reserve commas/semicolons for clarity; avoid long compound bullets.</li>
                            <li>Numbers: use en dashes for ranges (e.g., 53–56) and an apostrophe for decades (’90s).</li>
                        </ul>
                    </section>

                    <section className="guidelines__section">
                        <h2>Style & Formatting Tips</h2>
                        <ul className="guidelines__list">
                            <li><strong>Italicize</strong> quoted text on slides.</li>
                            <li>Spell out Bible book names (e.g., John, Matthew, 1 Corinthians). No abbreviations.</li>
                            <li>Capitalize Church, Mass, Scripture, Sacraments when used theologically.</li>
                            <li>Avoid ALL CAPS and excessive bold; prefer emphasis via italics and line breaks.</li>
                            <li>Be precise with claims that require sourcing (miracles, statistics, historical assertions).</li>
                        </ul>
                    </section>

                    <section className="guidelines__section">
                        <h2>Footnotes: How to Use</h2>
                        <ol className="guidelines__steps">
                            <li>Use numbered <em>superscripts</em> (¹, ², ³ …) for <strong>non-inline</strong> sourcing (ideas/paraphrases, data, statistics, historical claims).</li>
                            <li><strong>Place the superscript immediately after the idea/quote</strong>: after the final punctuation of the sentence (period, comma, colon, semicolon), after the closing quotation mark, or after the closing parenthesis if the sentence ends in parentheses. Do <em>not</em> put the number before punctuation.</li>
                            <li><strong>Type the superscript</strong> using the editor’s x² button, and <strong>manually add a matching numbered entry</strong> on the <strong>References</strong> editor card at the bottom of the Editor page. That card becomes the final References slide.</li>
                            <li>Each superscript number should map to the same number on the final <strong>References</strong> slide.</li>
                            <li>Prefer official links (Vatican, USCCB, diocesan sites, publisher pages). Avoid bare URLs—hyperlink the title or source.</li>
                        </ol>
                    </section>

                    <section className="guidelines__section">
                        <h2>Slide Naming</h2>
                        <ul className="guidelines__list">
                            <li><strong>Content Slides</strong> → bullets or short paragraphs; use superscript footnotes as needed.</li>
                            <li><strong>References</strong> → final slide with numbered list of footnotes.</li>
                        </ul>
                    </section>
                </div>

                <footer className="guidelines__footer">
                    <button className="guidelines__closeBtn" onClick={onClose}>Got it</button>
                </footer>
            </div>
        </div>
    );
}
